import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TelrPaymentStatus } from 'src/app/helper/telr-payment-return.util';

/** Payload for the Telr payment status dialog. */
export interface TelrPaymentStatusDialogData {
  title: string;
  message: string;
  svgIcon: string;
  iconVariant: 'danger' | 'warning' | 'success';
  /** Optional highlighted note shown under the message. */
  note?: string;
}

/**
 * Telr-only payment status dialog (title + message + status icon).
 * Kept separate so shared success/warning dialogs stay unchanged.
 */
@Component({
  standalone: false,
  selector: 'xa-telr-payment-status-dialog',
  templateUrl: './telr-payment-status-dialog.component.html',
  styleUrls: [ './telr-payment-status-dialog.component.scss' ],
  encapsulation: ViewEncapsulation.None,})
export class TelrPaymentStatusDialogComponent {
  /**
   * @param dialogRef Dialog ref used to close on OK.
   * @param data Title, message, and icon presentation for the Telr status.
   */
  constructor(
    public dialogRef: MatDialogRef<TelrPaymentStatusDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TelrPaymentStatusDialogData
  ) {}

  /**
   * Maps a Telr payment status to dialog title, message, and icon presentation.
   * @param status Normalized Telr payment status.
   * @returns Dialog payload for the matching status.
   */
  static buildData(status: TelrPaymentStatus): TelrPaymentStatusDialogData {
    switch (status) {
      case TelrPaymentStatus.Authorised:
        return {
          title: 'telr_payment_authorised_title',
          message: 'telr_payment_authorised_message',
          svgIcon: 'check-circle',
          iconVariant: 'success',
          note: 'telr_payment_authorised_note'
        };
      case TelrPaymentStatus.Declined:
        return {
          title: 'telr_payment_declined_title',
          message: 'telr_payment_declined_message',
          svgIcon: 'x-circle',
          iconVariant: 'danger'
        };
      case TelrPaymentStatus.Cancelled:
      default:
        return {
          title: 'telr_payment_cancelled_title',
          message: 'telr_payment_cancelled_message',
          svgIcon: 'warning-outline',
          iconVariant: 'warning'
        };
    }
  }

  /**
   * Opens the Telr payment status dialog for the given status.
   * @param dialog Material dialog service.
   * @param status Normalized Telr payment status.
   * @returns Dialog ref for the opened status dialog.
   */
  static open(
    dialog: MatDialog,
    status: TelrPaymentStatus
  ): MatDialogRef<TelrPaymentStatusDialogComponent> {
    return dialog.open(TelrPaymentStatusDialogComponent, {
      disableClose: true,
      autoFocus: false,
      width: '400px',
      panelClass: 'telr-payment-status-dialog',
      data: TelrPaymentStatusDialogComponent.buildData(status)
    });
  }

  /**
   * Closes the dialog.
   */
  close(): void {
    this.dialogRef.close(true);
  }
}
